import { Table } from "../ui/table";
import {
  addMonthsFromDate,
  formatDate,
  Pagination,
  useToastHelper,
} from "@/helpers/helperFunctions";
import { ColumnDef } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import Slider from "react-slick";
import {
  getScannerDeploymentsRequest,
  undeployScannerRequest,
} from "../api/scanner";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import ViewModal from "@/sections/build/components/StrategyViewModal";
import { Eye } from "lucide-react";
import Link from "next/link";

const PAGE_SIZE = 5;
const MONTHS_TO_EXPIRE = 3;
const settings = {
  arrows: true,
  dots: false,
  slidesToShow: 1,
  slidesToScroll: 1,
  variableWidth: true,
  speed: 500,
  initialSlide: 0,
  infinite: false,
};

export default function DeployedScannersTable({ userInfo }: { userInfo: any }) {
  const [page, setPage] = useState<number>(1);
  const [tableData, setTableData] = useState<any[]>([]);
  const { toast } = useToastHelper();
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selected, setSelected] = useState<any>({});

  const headers = useMemo(
    () => [
      {
        label: "Scanner",
        accessorKey: "scanner",
      },
      {
        label: "Universe",
        accessorKey: "universe",
      },
      {
        label: "Strategy",
        accessorKey: "strategy",
      },
      {
        label: "Deployed At",
        accessorKey: "createdAt",
      },
      {
        label: "Expires At",
        accessorKey: "expiresAt",
      },
    ],
    []
  );

  const handleViewScanner = (
    canvasData: string,
    symbol: string,
    timeframe: string
  ) => {
    const parsedCanvasData = JSON.parse(canvasData) || {};
    setSelected({
      nodes: parsedCanvasData?.filter?.(
        (item: any) => item.type === "selectorNode"
      ),
      edges: parsedCanvasData?.filter?.(
        (item: any) => item.type === "buttonedge"
      ),
      symbol: symbol,
      timeframe: timeframe,
    });
    setShowModal(true);
  };

  const getScannerDeployments = useCallback(async () => {
    const { response, error } = await getScannerDeploymentsRequest(
      userInfo,
      page,
      PAGE_SIZE
    );
    if (error) {
      console.error(error);
    } else {
      setTableData(
        response?.items?.map((scanner) => ({
          deploymentId: scanner.id,
          scanner: scanner.strategy.strategy_name,
          universe: scanner.universe.meta.name,
          createdAt: formatDate(scanner.last_updated_at, "DD-MMM-YY hh:mm"),
          symbolList: scanner.universe.universe_list,
          expiresAt: formatDate(
            addMonthsFromDate(scanner.last_updated_at, MONTHS_TO_EXPIRE),
            "DD-MMM-YY hh:mm"
          ),
          canvasData: scanner.strategy.canvas_data,
          timeframe: scanner.strategy.timeframe,
          symbol: scanner.strategy.symbol,
        })) || []
      );
      setTotalCount(response?.total || 0);
    }
  }, [userInfo, page, PAGE_SIZE]);

  const undeployScanner = useCallback(
    async (deploymentId: string) => {
      const { response, error } = await undeployScannerRequest(
        deploymentId,
        userInfo
      );
      if (error) {
        toast.error("Error undeploying scanner");
        console.error(error);
      } else {
        toast.success("Scanner undeployed successfully");
        getScannerDeployments();
      }
    },
    [userInfo, getScannerDeployments]
  );

  useEffect(() => {
    if (!userInfo) return;
    getScannerDeployments();
  }, [userInfo, getScannerDeployments]);

  useEffect(() => {
    getScannerDeployments();
  }, [page]);

  const createBadgesFromArray = (array: string[]) => {
    return array.map((item, index) => (
      <div className="mr-2" key={index}>
        <Badge variant="outline">{item}</Badge>
      </div>
    ));
  };
  if (tableData?.length <= 0) {
    return (
      <div className="flex items-center justify-center p-8 flex-col">
        <p className="text-slate-600">No scanner deployments found</p>
        <Link
          href="/test?tab=scanners"
          className="flex underline hover:underline text-[var(--marketplace-blue-new)] text-sm font-bold"
        >
          Click to create one now
        </Link>
      </div>
    );
  }

  return (
    <div>
      <ScrollArea className="h-fit min-h-[200px] mt-6">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Scanner</TableHead>
              <TableHead></TableHead>
              <TableHead>Group</TableHead>
              <TableHead>Deployed</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>Manage</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tableData.map((row) => (
              <>
                <TableRow key={row.deploymentId} className="border-b-0">
                  <TableCell>{row.scanner}</TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      onClick={() =>
                        handleViewScanner(
                          row.canvasData,
                          row.symbol,
                          row.timeframe
                        )
                      }
                    >
                      <div className="flex text-muted-foreground w-full items-center gap-2">
                        <Eye />
                        <p className="text-xs">View</p>
                      </div>
                    </Button>
                  </TableCell>
                  <TableCell>{row.universe}</TableCell>
                  <TableCell>{row.createdAt}</TableCell>
                  <TableCell>{row.expiresAt}</TableCell>
                  <TableCell>
                    {/* Scoped AlertDialog for each row */}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="secondary" className="lg:w-full">
                          Undeploy
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            Are you sure you want to undeploy?
                          </AlertDialogTitle>
                        </AlertDialogHeader>
                        <p>
                          This action cannot be undone, and the scanner will be
                          un-deployed immediately.
                        </p>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <Button
                            onClick={() => undeployScanner(row.deploymentId)}
                          >
                            Undeploy
                          </Button>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="max-w-80 test-page-slider flex items-center w-full"
                  >
                    <p className="text-muted-foreground whitespace-nowrap">
                      Running on:
                    </p>
                    <div className="px-4 flex flex-row justify-start items-center w-full">
                      <Slider
                        {...settings}
                        className="flex items-center ml-4 w-full"
                      >
                        {createBadgesFromArray(row.symbolList || [])}
                      </Slider>
                    </div>
                  </TableCell>
                </TableRow>
              </>
            ))}
          </TableBody>
        </Table>
        <ViewModal
          isOpen={showModal}
          onRequestClose={() => setShowModal(false)}
          selected={selected}
        />
        <Pagination
          totalCount={totalCount}
          currentPage={page}
          changePage={setPage}
          limit={PAGE_SIZE}
        />
      </ScrollArea>
    </div>
  );
}
